/**
 * Cognito's `ConfirmSignUp` throws `NotAuthorizedException` with the message
 * "User cannot be confirmed. Current status is CONFIRMED." when the account
 * has already been confirmed.
 *
 * **This is not on its own a success signal.** Email verification is
 * idempotent, so "already confirmed" *can* mean the submitted code simply
 * arrived after the account was confirmed: the verification email contains a
 * GET-fetchable confirmation link, so a mail-security link scanner / preview
 * pane (now that the SES-sent email lands in the inbox rather than spam — see
 * #2040) can fetch it and confirm the account moments before the user enters
 * the code by hand. But the message names no account, so it says nothing about
 * *whose* status is confirmed: the identical error is thrown when the code was
 * submitted against a different, already-confirmed account, leaving the user's
 * own account UNCONFIRMED (#2406).
 *
 * Callers must therefore pair this with evidence that the account confirmed is
 * the one the user came to confirm — see `confirm-signup.tsx`, which takes the
 * idempotency shortcut only for the identifier it put on the page itself.
 */
export function isAlreadyConfirmedError(err: unknown): boolean {
  if (!(err instanceof Error)) return false
  return /current status is confirmed/i.test(err.message)
}

/**
 * Cognito throws `UserNotFoundException` ("Username/client id combination not
 * found.") when `confirmSignUp` / `resendSignUpCode` is called with an
 * identifier it can't resolve to a user. During confirm-signup the identifier
 * may be an email, which Cognito can't resolve to its unverified alias yet — we
 * use this to decide when to fall back to a server-side email→username resolve
 * and retry. See #2120.
 */
export function isUserNotFoundError(err: unknown): boolean {
  if (!(err instanceof Error)) return false
  return (
    err.name === 'UserNotFoundException' ||
    /username\/client id combination not found/i.test(err.message)
  )
}
